import React from "react";
import { createApi } from "../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { addSelectedParam } from "./addSelectedParam";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { totalUpPrecisionRecallData } from "./totalUpPrecisionRecallData";
import { timeSlicer } from "../../Alerts/timeSlicer";
import { clipResultParam } from "./clipResultParam";

let activeSentinelQueryId = "";

// The page-1 request currently in flight, keyed by its URL. On a cold load
// LaunchDarklyProvider re-parents the app once it initializes, which remounts
// <Clips> and launches a second, identical fetch chain (BT-1070). The second
// chain wins the `activeSentinelQueryId` race and used to fire its own copy of
// a 10–20s request, so the user always waited for the slower of the two.
// Sharing the in-flight promise lets the surviving chain consume the response
// the first chain already paid for (ENG-1502).
let inFlightInitialSentinelRequest: {
  url: string;
  promise: Promise<AxiosResponse>;
} | null = null;

// Returns the subset of `results` whose file_id is not already present in
// `previousState`. Guards against duplicate rows — and the inflated clip
// counts / precision-recall totals they cause — when more than one paginator
// chain accumulates through the same state setters (see BT-1070 above).
//
// Callers assign this helper's return value to a local from INSIDE a
// setSentinelData updater and branch on it right after. That relies on React
// 17 running the updater synchronously when setState is called outside event
// handlers. Under React 18 automatic batching the updater is deferred, the
// local would still be empty at the branch, and the precision/recall summary
// would silently never be folded — restructure this pattern before upgrading.
const dedupeNewSentinelResults = (previousState: any[], results: any[]) => {
  const existingFileIds = new Set(
    (previousState || []).map((clip: any) => clip && clip.file_id)
  );
  return (results || []).filter(
    (clip: any) => clip && clip.file_id && !existingFileIds.has(clip.file_id)
  );
};

export const formatSentinalDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // getMonth() returns 0-11, +1 to make it 1-12
  const day = date.getDate().toString().padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const storeSentinelCursor = (lastFileId: string, lastTimeStamp: string) => {
  localStorage.setItem("last_file_id_sentinal", JSON.stringify(lastFileId));
  localStorage.setItem(
    "last_timestamp_sentinal",
    JSON.stringify(lastTimeStamp)
  );
};

export const getSentinelClipsWithQuerry = (
  setSentinelData: any,
  setPageLoadingSentinel: Dispatcher<boolean>,
  setSelectedRowSentinel: any,
  setPrecisionRecallDataSentinel: any,
  setVideoURLSentinel: any,
  setVideoClipLoadingSentinel: Dispatcher<boolean>,
  setClipsIndexOrderSentinel: any,
  selectedRowSentinel: any,
  setNoDataToDisplaySentinel: Dispatcher<boolean>,
  setSiteOptionsSentinel: any,
  setLoadingSitesSentinel: any,
  userFiltersSentinel: any,
  setFetchingFilteredDataSentinel: Dispatcher<boolean>,
  setPageSentinel: any,
  lastTimeStamp: string,
  lastFileId: string,
  moreThan100: boolean,
  setLoadingAdditionalData: Dispatcher<boolean>,
  navigate: NavigateFunction,
  queryId?: string
) => {
  const isInitialQuery = !moreThan100 && !lastTimeStamp && !lastFileId;
  if (queryId && queryId !== activeSentinelQueryId) {
    // A retry or next page from a chain that has been superseded — by the
    // LaunchDarkly remount or by the user applying new filters. Letting it
    // run again used to re-activate it below, so a token-refresh retry from
    // the unmounted instance could win the race and the live instance's own
    // response was then discarded, leaving the spinner up forever (ENG-1528).
    return;
  }
  const effectiveQueryId =
    queryId ??
    (isInitialQuery
      ? `${Date.now()}-${Math.random().toString(36).slice(2)}`
      : activeSentinelQueryId);
  if (isInitialQuery && !queryId) {
    // Only a brand-new initial query becomes the active chain; a retry keeps
    // whatever id it was given and must not re-activate it.
    activeSentinelQueryId = effectiveQueryId;
    setPageSentinel(0);
  }
  const convertTimeToUTCFormat = (time: string): string => {
    const [hours, minutes, seconds] = time.split(":");
    return encodeURIComponent(`${hours}:${minutes}`);
  };
  const shouldDeferInitialSelectionForUrlFileId = (() => {
    if (typeof window === "undefined") return false;
    if (!window.location.pathname.includes("/clips")) return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") === "sentinel" && !!params.get("file_id");
  })();

  const AxiosUI = createApi("");

  const retryFunction = () => {
    getSentinelClipsWithQuerry(
      setSentinelData,
      setPageLoadingSentinel,
      setSelectedRowSentinel,
      setPrecisionRecallDataSentinel,
      setVideoURLSentinel,
      setVideoClipLoadingSentinel,
      setClipsIndexOrderSentinel,
      selectedRowSentinel,
      setNoDataToDisplaySentinel,
      setSiteOptionsSentinel,
      setLoadingSitesSentinel,
      userFiltersSentinel,
      setFetchingFilteredDataSentinel,
      setPageSentinel,
      lastTimeStamp,
      lastFileId,
      moreThan100,
      setLoadingAdditionalData,
      navigate,
      effectiveQueryId
    );
  };

  const fetchNextPage = (nextTimeStamp: string, nextFileId: string) => {
    getSentinelClipsWithQuerry(
      setSentinelData,
      setPageLoadingSentinel,
      setSelectedRowSentinel,
      setPrecisionRecallDataSentinel,
      setVideoURLSentinel,
      setVideoClipLoadingSentinel,
      setClipsIndexOrderSentinel,
      selectedRowSentinel,
      setNoDataToDisplaySentinel,
      setSiteOptionsSentinel,
      setLoadingSitesSentinel,
      userFiltersSentinel,
      setFetchingFilteredDataSentinel,
      setPageSentinel,
      nextTimeStamp,
      nextFileId,
      true,
      setLoadingAdditionalData,
      navigate,
      effectiveQueryId
    );
  };

  let searchParamsString = ``;

  if (userFiltersSentinel.site_ids[0]) {
    searchParamsString += `&site_ids=${userFiltersSentinel.site_ids[0]}`;
  }

  if (userFiltersSentinel.camera_ids[0]) {
    searchParamsString += `&camera_ids=${userFiltersSentinel.camera_ids[0]}`;
  }

  if (userFiltersSentinel.start_date) {
    searchParamsString += `&start_date=${formatSentinalDate(
      userFiltersSentinel.start_date
    )}`;
  }

  if (userFiltersSentinel.end_date) {
    searchParamsString += `&end_date=${formatSentinalDate(
      userFiltersSentinel.end_date
    )}`;
  }

  if (
    userFiltersSentinel.start_date.toString() ===
      userFiltersSentinel.end_date.toString() &&
    userFiltersSentinel.start_time !== null
  ) {
    searchParamsString += `&start_time=${convertTimeToUTCFormat(
      timeSlicer(userFiltersSentinel.start_time)
    )}`;
  }

  if (
    userFiltersSentinel.start_date.toString() ===
      userFiltersSentinel.end_date.toString() &&
    userFiltersSentinel.end_time !== null
  ) {
    searchParamsString += `&end_time=${convertTimeToUTCFormat(
      timeSlicer(userFiltersSentinel.end_time)
    )}`;
  }

  searchParamsString += clipResultParam(userFiltersSentinel.clip_result);

  if (lastTimeStamp) {
    searchParamsString += `&last_timestamp=${lastTimeStamp}`;
  }

  if (lastFileId) {
    searchParamsString += `&last_file_id=${lastFileId}`;
  }

  searchParamsString += `&page_size=100`;

  const requestUrl = `clip/?username=${userFiltersSentinel.user}${searchParamsString}`;

  const stillOnClipsPage = () => window.location.href.includes("clips");

  // Every chain ends here: nothing more to fetch (or nothing more we can fetch).
  const finishPagination = () => {
    setFetchingFilteredDataSentinel(false);
    setLoadingAdditionalData(false);
  };

  // Merge this page's rows into state and fold its summary into the running
  // precision/recall totals — only when the page actually contributed new
  // clips, so a duplicate page fetched by a racing chain can't inflate them.
  const appendPage = (results: any[], summary: any) => {
    setNoDataToDisplaySentinel(false);
    let freshResults: any[] = [];
    setSentinelData((previousState: any) => {
      freshResults = dedupeNewSentinelResults(previousState, results);
      return addSelectedParam(
        [...previousState, ...freshResults],
        setClipsIndexOrderSentinel,
        lastFileId
      );
    });
    if (freshResults.length > 0) {
      const responsePrecisionRecall = totalUpPrecisionRecallData(summary);
      setPrecisionRecallDataSentinel((previousState: any) => {
        return {
          date: responsePrecisionRecall.date,
          date_str: responsePrecisionRecall.date_str,
          true_positives:
            responsePrecisionRecall.true_positives +
            previousState.true_positives,
          false_positives:
            responsePrecisionRecall.false_positives +
            previousState.false_positives,
          false_negatives:
            responsePrecisionRecall.false_negatives +
            previousState.false_negatives,
          true_negatives:
            responsePrecisionRecall.true_negatives +
            previousState.true_negatives,
          unclassified:
            responsePrecisionRecall.unclassified + previousState.unclassified,
          total_clips:
            responsePrecisionRecall.total_clips + previousState.total_clips,
          precision:
            previousState.precision || responsePrecisionRecall.precision,
          recall: previousState.recall || responsePrecisionRecall.recall,
        };
      });
    }
  };

  // A full page means there may be more. Prefer the cursor the API hands back.
  // Older admin builds answer `count == page_size` with a null cursor (the
  // DynamoDB key was dropped while accumulating filtered pages, ENG-1501); in
  // that case derive one from the OLDEST row of this page so the next request
  // starts where this one ended. The old fallback keyed off `results[0]`, the
  // NEWEST row, which re-fetched the same page shifted by a single clip —
  // one 8–12s request per additional clip.
  const continuePagination = (results: any[], data: any) => {
    if (!stillOnClipsPage()) {
      finishPagination();
      return;
    }
    if (data.last_timestamp && data.last_file_id) {
      storeSentinelCursor(data.last_file_id, data.last_timestamp);
      fetchNextPage(data.last_timestamp, data.last_file_id);
      return;
    }
    const oldestRow = results[results.length - 1];
    if (!oldestRow || !oldestRow.file_id) {
      finishPagination();
      return;
    }
    AxiosUI.get(
      `clip/info/?username=${userFiltersSentinel.user}&file_id=${oldestRow.file_id}`
    ).then(
      (infoResponse: AxiosResponse) => {
        if (
          infoResponse.data.event_timestamp &&
          infoResponse.data.file_id &&
          stillOnClipsPage()
        ) {
          storeSentinelCursor(
            infoResponse.data.file_id,
            infoResponse.data.event_timestamp
          );
          fetchNextPage(
            infoResponse.data.event_timestamp,
            infoResponse.data.file_id
          );
        } else {
          finishPagination();
        }
      },
      () => finishPagination()
    );
  };

  // Select the first clip of a fresh result set and load its video, then
  // hand off to `onVideoReady` (continue paging, or finish).
  const selectFirstRow = (results: any[], onVideoReady: () => void) => {
    setSelectedRowSentinel(results[0]);
    AxiosUI.get(
      `clip/info/?username=${userFiltersSentinel.user}&file_id=${results[0].file_id}`
    ).then(
      (infoResponse: AxiosResponse) => {
        if (!infoResponse.data.file_id) {
          setFetchingFilteredDataSentinel(false);
          setPageLoadingSentinel(false);
          onVideoReady();
          return;
        }
        // One merged update. A setState per field re-rendered <Clips> once per
        // field (React 17 does not batch outside event handlers) — a multi-second
        // main-thread stall between clip/info and clip/url (ENG-1508).
        setSelectedRowSentinel((previousState: any) => ({
          ...previousState,
          ...infoResponse.data,
        }));
        localStorage.setItem(
          "investigationSelectedRow",
          JSON.stringify(selectedRowSentinel)
        );
        AxiosUI.get(
          `clip/url/?username=${userFiltersSentinel.user}&file_id=${infoResponse.data.file_id}`
        ).then(
          (urlResponse: AxiosResponse) => {
            setVideoURLSentinel(urlResponse.data);
            setVideoClipLoadingSentinel(false);
            setFetchingFilteredDataSentinel(false);
            setPageLoadingSentinel(false);
            onVideoReady();
          },
          (reason: AxiosError) => {
            universalAPIErrorHandler(reason, navigate, retryFunction, () => {
              setNoDataToDisplaySentinel(false);
              setVideoClipLoadingSentinel(false);
              setFetchingFilteredDataSentinel(false);
              setLoadingAdditionalData(false);
            });
          }
        );
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          setVideoClipLoadingSentinel(false);
          setFetchingFilteredDataSentinel(false);
          setPageLoadingSentinel(false);
          setLoadingAdditionalData(false);
        });
      }
    );
  };

  let request: Promise<AxiosResponse>;
  if (
    isInitialQuery &&
    inFlightInitialSentinelRequest &&
    inFlightInitialSentinelRequest.url === requestUrl
  ) {
    request = inFlightInitialSentinelRequest.promise;
  } else {
    request = AxiosUI.get(requestUrl);
    if (isInitialQuery) {
      const entry = { url: requestUrl, promise: request };
      inFlightInitialSentinelRequest = entry;
      const release = () => {
        if (inFlightInitialSentinelRequest === entry) {
          inFlightInitialSentinelRequest = null;
        }
      };
      request.then(release, release);
    }
  }

  request.then(
    (response: AxiosResponse) => {
      if (!effectiveQueryId || effectiveQueryId !== activeSentinelQueryId) {
        return;
      }
      let lastFileIdSentinal = "";
      if (localStorage.last_file_id_sentinal) {
        lastFileIdSentinal = JSON.parse(localStorage.last_file_id_sentinal);
      }
      let lastTimeStampSentinal = "";
      if (localStorage.last_timestamp_sentinal) {
        lastTimeStampSentinal = JSON.parse(
          localStorage.last_timestamp_sentinal
        );
      }
      const isCurrentCursor =
        (lastFileIdSentinal === lastFileId &&
          lastTimeStampSentinal === lastTimeStamp) ||
        !lastFileId ||
        !lastTimeStamp;
      if (!isCurrentCursor) {
        return;
      }

      const results: any[] = response.data.results || [];
      const hasResults = !!results[0];

      if (!hasResults) {
        if (!moreThan100) {
          setNoDataToDisplaySentinel(true);
        }
        finishPagination();
        return;
      }

      appendPage(results, response.data.summary);
      if (!moreThan100) {
        // The rows are what the user is waiting for; the video pane shows its
        // own loading state until clip/url resolves (ENG-1508).
        setFetchingFilteredDataSentinel(false);
        setPageLoadingSentinel(false);
      }

      const isFullPage = response.data.count === 100;
      const shouldSelectFirstRow =
        !moreThan100 && !shouldDeferInitialSelectionForUrlFileId;

      if (isFullPage) {
        if (shouldSelectFirstRow) {
          selectFirstRow(results, () =>
            continuePagination(results, response.data)
          );
        } else {
          continuePagination(results, response.data);
        }
        return;
      }

      // Short page: this is the last one.
      if (shouldSelectFirstRow) {
        selectFirstRow(results, finishPagination);
      } else {
        if (!moreThan100) {
          setPageLoadingSentinel(false);
        }
        finishPagination();
      }
    },
    (reason: AxiosError) => {
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        setFetchingFilteredDataSentinel(false);
        setPageLoadingSentinel(false);
        setLoadingAdditionalData(false);
      });
    }
  );
};
